import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Stepper, Step, StepLabel, StepContent, Button, Tooltip } from "@mui/material";
import FormField from "../../components/FormComponent/FormField/FormField";
import styles from "./styles/styles.module.css";
import { clinicDetails } from "../../JsonForms/ClinicDetails";
import ImageUploadModal from "./ImageUploadModal";
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import cogoToast from "cogo-toast";
import { axiosProxyInstance } from "../../utils/request";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorIcon from '@mui/icons-material/Error';

const defaultValidationSchema = yup.object({
  openingTime: yup
    .string()
    .required('Opening time is required')
    .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
    .test('is-before-closing', 'Opening time must be before closing time', function (value) {
      const { closingTime } = this.parent;
      if (closingTime && value) {
        return new Date(`1970-01-01T${value}Z`) < new Date(`1970-01-01T${closingTime}Z`);
      }
      return true;
    }),

  closingTime: yup
    .string()
    .required('Closing time is required')
    .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
    .test('is-after-opening', 'Closing time must be after opening time', function (value) {
      const { openingTime } = this.parent;
      if (openingTime && value) {
        return new Date(`1970-01-01T${value}Z`) > new Date(`1970-01-01T${openingTime}Z`);
      }
      return true;
    }),

  daysOfWeek: yup.array().min(1, 'At least one day should be selected'),
  initialAssessmentDuration: yup.number().nullable().positive('Duration must be a positive number').required('Initial assessment duration is required'),
  initialAssessmentPrice: yup.number().nullable().positive('Price must be a positive number').required('Initial assessment price is required'),
  followUpTreatmentDuration: yup.number().nullable().positive('Duration must be a positive number').required('FollowUp treatment duration is required'),
  followUpTreatmentPrice: yup.number().nullable().positive('Duration must be a positive number').required('FollowUp treatment price is required'),
  availableParking: yup.array().min(1, 'Select at least one parking option').required('Parking options are required'),
  accessibleAccess: yup.string().required('Accessible access selection is required'),
  // insuranceAccepted: yup.array(),
  // insuranceAccepted: yup.array().min(1, 'Select at least one insurance').required('At least one insurance must be selected'),
  clinicDescription: yup.string().max(3000, 'Description cannot exceed 3000 characters')
    .test(
      "not-only-spaces",
      "This field cannot contain only blank spaces",
      (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    ),
});

const generateValidationSchema = (values) => {
  const schemaShape = {
    availableParking: yup
      .array()
      .min(1, "Select at least one parking option")
      .required("Parking options are required"),

    accessibleAccess: yup
      .string()
      .required("Accessible access selection is required"),

    clinicDescription: yup
      .string()
      .max(3000, "Description cannot exceed 3000 characters")
      .test(
        "not-only-spaces",
        "This field cannot contain only blank spaces",
        (value) => (value ? value.trim().length > 0 : true)
      ),

    openingTime: yup
      .string()
      .required('Opening time is required')
      .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
      .test('is-before-closing', 'Opening time must be before closing time', function (value) {
        const { closingTime } = this.parent;
        if (closingTime && value) {
          return new Date(`1970-01-01T${value}Z`) < new Date(`1970-01-01T${closingTime}Z`);
        }
        return true;
      }),

    closingTime: yup
      .string()
      .required('Closing time is required')
      .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
      .test('is-after-opening', 'Closing time must be after opening time', function (value) {
        const { openingTime } = this.parent;
        if (openingTime && value) {
          return new Date(`1970-01-01T${value}Z`) > new Date(`1970-01-01T${openingTime}Z`);
        }
        return true;
      }),

    daysOfWeek: yup.array().min(1, 'At least one day should be selected'),

    initialAssessmentDuration: yup
      .number()
      .nullable()
      .positive('Duration must be a positive number')
      .required('Initial assessment duration is required'),

    initialAssessmentPrice: yup
      .number()
      .nullable()
      .positive('Price must be a positive number')
      .required('Initial assessment price is required'),

    followUpTreatmentDuration: yup
      .number()
      .nullable()
      .positive('Duration must be a positive number')
      .required("FollowUp treatment duration is required"),

    followUpTreatmentPrice: yup
      .number()
      .nullable()
      .positive('Price must be a positive number')
      .required("FollowUp treatment price is required"),
  };

  Object.keys(values).forEach((key) => {
    // Opening & closing time pair validation
    const match = key.match(/^openingTime(-\d+)?$/);
    if (match) {
      const suffix = match[1] || "";
      const openingKey = `openingTime${suffix}`;
      const closingKey = `closingTime${suffix}`;

      schemaShape[openingKey] = yup
        .string()
        .required("Opening time is required")
        .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
        .test(
          "is-before-closing",
          "Opening time must be before closing time",
          function (value) {
            const closingValue = this.parent[closingKey];
            if (value && closingValue) {
              return (
                new Date(`1970-01-01T${value}Z`) <
                new Date(`1970-01-01T${closingValue}Z`)
              );
            }
            return true;
          }
        );

      schemaShape[closingKey] = yup
        .string()
        .required("Closing time is required")
        .matches(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Opening time must be a valid time (hh:mm AM/PM)")
        .test(
          "is-after-opening",
          "Closing time must be after opening time",
          function (value) {
            const openingValue = this.parent[openingKey];
            if (value && openingValue) {
              return (
                new Date(`1970-01-01T${value}Z`) >
                new Date(`1970-01-01T${openingValue}Z`)
              );
            }
            return true;
          }
        );
    }

    // Days of the week array
    const dayMatch = key.match(/^daysOfWeek(-\d+)?$/);
    if (dayMatch) {
      schemaShape[key] = yup
        .array()
        .min(1, "At least one day should be selected");
    }

    // Dynamic assessment fields
    if (key.startsWith("initialAssessmentDuration")) {
      schemaShape[key] = yup
        .number()
        .nullable()
        .positive("Duration must be a positive number")
        .required("Initial assessment duration is required");
    }

    if (key.startsWith("initialAssessmentPrice")) {
      schemaShape[key] = yup
        .number()
        .nullable()
        .positive("Price must be a positive number")
        .required("Initial assessment price is required");
    }

    // Dynamic assessment fields
    if (key.startsWith("followUpTreatmentDuration")) {
      schemaShape[key] = yup
        .number()
        .nullable()
        .positive("Duration must be a positive number")
        .required("FollowUp treatment duration is required");
    }

    if (key.startsWith("followUpTreatmentPrice")) {
      schemaShape[key] = yup
        .number()
        .nullable()
        .positive("Price must be a positive number")
        .required("FollowUp treatment price is required");
    }

    // if (key.startsWith("assessmentType")) {
    //   schemaShape[key] = yup.string().required("Assessment type is required");
    // }
  });

  return yup.object().shape(schemaShape);
};

const ClinicDetails = ({ goToStep, clinicRegistrationData = null }) => {
  const [clinicDetailsData, setClinicDetailsData] = useState(JSON.parse(JSON.stringify(clinicDetails)) || { sections: [], actions: [] });
  const [src, setSrc] = useState(null);
  const [open, setOpen] = useState(false);
  const [currentField, setCurrentField] = useState(null);
  const [validationSchema, setValidationSchema] = useState(() => defaultValidationSchema);
  const [isDefaultValueSet, setIsDefaultValueSet] = useState(false);

  const resolver = useMemo(() => yupResolver(validationSchema), [validationSchema]);
  const form = useForm({
    resolver,
    mode: "onBlur",
  });
  const { register, handleSubmit, control, formState: { errors }, reset, watch, setValue, getValues } = form;
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleUpdateFormField = (assessmentArray, treatmentArray, slotsArray, logoUrl = null) => {
    let updatedClinicDetails = Object.assign({}, JSON.parse(JSON.stringify(clinicDetails))); // deep clone
    let isAssessmentAdded = false;
    let isTreatmentAdded = false;
    let isSlotsAdded = false;
    // if (field.action === "handleImageChange") {
    //   setCurrentField(field);
    //   setSrc(field.src || null);
    //   setOpen(true);
    // }
    updatedClinicDetails.sections.forEach((section) => {
      section.fields.forEach((fieldGroup, index) => {
        if(fieldGroup.id === "clinicLogo"){
          fieldGroup.src = logoUrl || "";
          setCurrentField(fieldGroup);
          setSrc(logoUrl || null);
          fieldGroup = fieldGroup;
        }
        if (fieldGroup.id === "addAssessment" && fieldGroup.fields) {
          assessmentArray.forEach((item, assessmentIndex) => {
            if(assessmentIndex > 0 && !isAssessmentAdded){
              const dynamicFieldsCount = fieldGroup.fields.filter(
                (f) => f.group === "assessment"
              ).length;
              const newAssessmentGroup = {
                group: "assessment",
                deleteId: `assessment-${dynamicFieldsCount + 1}`,
                fields: [
                  {
                    label: "Enter duration",
                    placeholder: "e.g. 30",
                    unit: "mins",
                    type: "number-unit",
                    // required: true,
                    id: `initialAssessmentDuration-${dynamicFieldsCount + 1}`,
                  },
                  {
                    label: "Enter price",
                    placeholder: "e.g. 50",
                    unit: "£",
                    type: "number-unit",
                    // required: true,
                    id: `initialAssessmentPrice-${dynamicFieldsCount + 1}`,
                  },
                ],
              };
              const newAssessmentTypeSelector = {
                label: "Select assessment type (optional)",
                placeholder: "Select an option",
                type: "dropdown",
                showInfoIcon: true,
                infoMessage: "Please select the corresponding assessment type",
                options: [
                  "Home visit",
                  "Virtual visit",
                  "Consultant physio",
                  "Senior physio",
                ],
                // required: true,
                id: `assessmentType-${dynamicFieldsCount + 1}`,
                deleteId: `assessment-${dynamicFieldsCount + 1}`,
                isDynamic: true,
              }
              isAssessmentAdded = true;
              fieldGroup.fields.splice(fieldGroup.fields?.length - 1, 0, newAssessmentGroup);
              fieldGroup.fields.splice(fieldGroup.fields?.length - 1, 0, newAssessmentTypeSelector);

            }
          });
        }
        if (fieldGroup.id === "addTreatment" && fieldGroup.fields) {
          treatmentArray.forEach((item, treatmentIndex) => {
            if(treatmentIndex > 0 && !isTreatmentAdded){
              const dynamicFieldsCount = fieldGroup.fields.filter(
                (f) => f.group === "treatment"
              ).length;
              const newTreatmentGroup = {
                group: "treatment",
                deleteId: `treatment-${dynamicFieldsCount + 1}`,
                fields: [
                  {
                    label: "Enter duration",
                    placeholder: "e.g. 30",
                    unit: "mins",
                    type: "number-unit",
                    // required: true,
                    id: `followUpTreatmentDuration-${dynamicFieldsCount + 1}`,
                  },
                  {
                    label: "Enter price",
                    placeholder: "e.g. 50",
                    unit: "£",
                    type: "number-unit",
                    // required: true,
                    id: `followUpTreatmentPrice-${dynamicFieldsCount + 1}`,
                  },
                ],
              };
              const newTreatmentTypeSelector = {
                label: "Select follow-up treatment type (optional)",
                placeholder: "Select an option",
                type: "dropdown",
                showInfoIcon: true,
                infoMessage: "Please select the corresponding follow-up treatment type",
                options: [
                  "Home visit",
                  "Virtual visit",
                  "Consultant physio",
                  "Senior physio",
                ],
                // required: true,
                id: `followUpTreatmentType-${dynamicFieldsCount + 1}`,
                deleteId: `treatment-${dynamicFieldsCount + 1}`,
                isDynamic: true,
              };
              isTreatmentAdded = true;
              fieldGroup.fields.splice(fieldGroup.fields?.length - 1, 0, newTreatmentGroup);
              fieldGroup.fields.splice(fieldGroup.fields?.length - 1, 0, newTreatmentTypeSelector);
            }
          });
        }
        if (fieldGroup.id === "addTimeSlotId" && fieldGroup.fields) {
          slotsArray.forEach((item, slotIndex) => {
            if(slotIndex > 0 && !isSlotsAdded){
              const dynamicFieldsCount = fieldGroup.fields.filter(
                (f) => f.group === "timeSlot"
              ).length;
              const newTimeSlotGroup = {
                group: "timeSlot",
                deleteId: `timeSlot-${dynamicFieldsCount + 1}`,
                fields: [
                  {
                    placeholder: "Opening time",
                    type: "time",
                    // required: true,
                    id: `openingTime-${dynamicFieldsCount + 1}`,
                  },
                  {
                    placeholder: "Closing time",
                    type: "time",
                    // required: true,
                    id: `closingTime-${dynamicFieldsCount + 1}`,
                  },
                ],
              };
              const newWeekdaySelector = {
                type: "no-border-checkbox",
                options: [{
                  "label": "Mon",
                  "value": "Mon"
                },
                {
                  "label": "Tue",
                  "value": "Tue"
                },
                {
                  "label": "Wed",
                  "value": "Wed"
                },
                {
                  "label": "Thu",
                  "value": "Thu"
                },
                {
                  "label": "Fri",
                  "value": "Fri"
                },
                {
                  "label": "Sat",
                  "value": "Sat"
                },
                {
                  "label": "Sun",
                  "value": "Sun"
                }],
                // required: true,
                id: `daysOfWeek-${dynamicFieldsCount + 1}`,
                deleteId: `timeSlot-${dynamicFieldsCount + 1}`,
                isDynamic: true,
                values: {
                  Mon: false,
                  Tue: false,
                  Wed: false,
                  Thu: false,
                  Fri: false,
                  Sat: false,
                  Sun: false,
                },
              };
              isSlotsAdded = true;
              fieldGroup.fields.push(newTimeSlotGroup);
              fieldGroup.fields.push(newWeekdaySelector);
            }
          });
        }
      });
    });
    setClinicDetailsData(updatedClinicDetails);
  };

  const setInitialFormData = async () => {
    let slots = [];
    // if (field.action === "handleImageChange") {
    //   setCurrentField(field);
    //   setSrc(field.src || null);
    //   setOpen(true);
    // }
    // if(clinicRegistrationData?.logo){
    //   setSrc(clinicRegistrationData?.logo)
    // }else{}
    const defaultOpeningHours = {};
    if (clinicRegistrationData.openingHours?.length) {
        slots = clinicRegistrationData.openingHours.map((item, index) => ({
            id: `${index + 1}`,
            from: item.from,
            to: item.to,
            days: item.days,
        }));
        slots.forEach((slot, slotIndex) => {
          if(slotIndex === 0){
            defaultOpeningHours[`openingTime`] = slot.from;
            defaultOpeningHours[`closingTime`] = slot.to;
            defaultOpeningHours[`daysOfWeek`] = slot.days;
          }else{
            defaultOpeningHours[`openingTime-${slot.id}`] = slot.from;
            defaultOpeningHours[`closingTime-${slot.id}`] = slot.to;
            defaultOpeningHours[`daysOfWeek-${slot.id}`] = slot.days; 
          }
        });
    }else{}
    const findSection = clinicDetails?.sections.find((item) => item.id === "insuranceAccepted");
    const findInsuranceList = findSection?.fields[0]?.options;
    // Extract keys from options
    const optionKeys = findInsuranceList.map(obj => Object.keys(obj)[0]);
    const manualInsuranceEntryFilter = clinicRegistrationData?.insuranceAccepted?.filter((item) => !optionKeys.includes(item));
    const insuranceEntryFilter = clinicRegistrationData?.insuranceAccepted?.filter((item) => optionKeys.includes(item));;
    
    let resetValueObject = {
      availableParking: clinicRegistrationData?.availableParking || [],
      accessibleAccess: clinicRegistrationData?.accessibleAccess || "",
      insuranceAccepted: insuranceEntryFilter || [],
      manualInsuranceEntry: manualInsuranceEntryFilter || [],
      clinicDescription: clinicRegistrationData?.clinicDescription || "",
      ...defaultOpeningHours,
    };

    const assessmentArray = Object.values(clinicRegistrationData?.initial_assessment || []);
    const treatmentArray = Object.values(clinicRegistrationData?.follow_up_treatment || []);
    const assessmentTypeArray = Object.values(clinicRegistrationData?.assessment_type || []);
    const followUpTypeArray = Object.values(clinicRegistrationData?.followup_treatment_type || []);

    if(assessmentArray?.length && treatmentArray?.length){
      if(!isDefaultValueSet){
        handleUpdateFormField(assessmentArray, treatmentArray, slots, clinicRegistrationData?.logo);
        setIsDefaultValueSet(true);
      }else{}
      const dynamicAssessmentFields = assessmentArray.map((assessment, assessmentIndex) => {
        Object.entries(assessment).forEach(([key, value]) => {
          const newKey = key.replace('Initial_', '').toLowerCase(); // Remove the "Initial_" prefix and lowercase the key
          if(newKey.includes('duration')){
            if(assessmentIndex === 0){
              const fieldId = `initialAssessmentDuration`;
              resetValueObject[fieldId] = value;
            }else{
              const fieldId = `initialAssessmentDuration-${assessmentIndex + 1}`;
              resetValueObject[fieldId] = value;
            }
          }else if(newKey.includes('cost')){
            if(assessmentIndex === 0){
              const fieldId = `initialAssessmentPrice`;
              resetValueObject[fieldId] = value;
            }else{
              const fieldId = `initialAssessmentPrice-${assessmentIndex + 1}`;
              resetValueObject[fieldId] = value;
            }
          }else{
            if(assessmentIndex === 0){
              const fieldId = `assessmentType`;
              resetValueObject[fieldId] = value;
            }else{
              const fieldId = `assessmentType-${assessmentIndex + 1}`;
              resetValueObject[fieldId] = value;
            }
          }
        });
        return assessment;
      });
      const dynamicTreatmentFields = treatmentArray.map((treatment, treatmentIndex) => {
        Object.entries(treatment).forEach(([key, value]) => {
          const newKey = key.replace('follow_up_treatment_', '').toLowerCase(); // Remove the "Initial_" prefix and lowercase the key
          if(newKey.includes('duration')){
            if(treatmentIndex === 0){
              const fieldId = `followUpTreatmentDuration`;
              resetValueObject[fieldId] = value;
            }else{
              const fieldId = `followUpTreatmentDuration-${treatmentIndex + 1}`;
              resetValueObject[fieldId] = value;
            }
          }else if(newKey.includes('cost')){
            if(treatmentIndex === 0){
              const fieldId = `followUpTreatmentPrice`;
              resetValueObject[fieldId] = value;
            }else{
              const fieldId = `followUpTreatmentPrice-${treatmentIndex + 1}`;
              resetValueObject[fieldId] = value;
            }
          }else{
            if(treatmentIndex === 0){
              const fieldId = `followUpTreatmentType`;
              resetValueObject[fieldId] = value;
            }else{
              const fieldId = `followUpTreatmentType-${treatmentIndex + 1}`;
              resetValueObject[fieldId] = value;
            }
          }
        });
        return treatment;
      })
    }else{
      if(assessmentTypeArray?.length){
        const dynamicAssessmentFields = assessmentTypeArray.map((assessment, assessmentIndex) => {
          if(assessmentIndex === 0){
            const durationFieldId = `initialAssessmentDuration`;
            const priceFieldId = `initialAssessmentPrice`;
            const typeFieldId = `assessmentType`;
            resetValueObject[durationFieldId] = clinicRegistrationData?.initial_appointment_time;
            resetValueObject[priceFieldId] = clinicRegistrationData?.initial_appointment_cost;
            resetValueObject[typeFieldId] = assessment;
          }else{
            const durationFieldId = `initialAssessmentDuration-${assessmentIndex + 1}`;
            const priceFieldId = `initialAssessmentPrice-${assessmentIndex + 1}`;
            const typeFieldId = `assessmentType-${assessmentIndex + 1}`;
            resetValueObject[durationFieldId] = null;
            resetValueObject[priceFieldId] = null;
            resetValueObject[typeFieldId] = assessment;
          }
          return assessment;
        });
      }
      if(followUpTypeArray?.length){
        const dynamicTreatmentFields = followUpTypeArray.map((treatment, treatmentIndex) => {
          if(treatmentIndex === 0){
            const durationFieldId = `followUpTreatmentDuration`;
            const priceFieldId = `followUpTreatmentPrice`;
            const typeFieldId = `followUpTreatmentType`;
            resetValueObject[durationFieldId] = clinicRegistrationData?.followup_appointment_time;
            resetValueObject[priceFieldId] = clinicRegistrationData?.followup_appointment_cost;
            resetValueObject[typeFieldId] = treatment;
          }else{
            const durationFieldId = `followUpTreatmentDuration-${treatmentIndex + 1}`;
            const priceFieldId = `followUpTreatmentPrice-${treatmentIndex + 1}`;
            const typeFieldId = `followUpTreatmentType-${treatmentIndex + 1}`;
            resetValueObject[durationFieldId] = null;
            resetValueObject[priceFieldId] = null;
            resetValueObject[typeFieldId] = treatment;
          }
          return treatment;
        });
      }
      if(assessmentTypeArray?.length === 0 && followUpTypeArray?.length === 0){
        const durationFieldId = `initialAssessmentDuration`;
        const priceFieldId = `initialAssessmentPrice`;
        const typeFieldId = `assessmentType`;
        resetValueObject[durationFieldId] = null;
        resetValueObject[priceFieldId] = null;
        resetValueObject[typeFieldId] = "";

        const ftDurationFieldId = `followUpTreatmentDuration`;
        const ftPriceFieldId = `followUpTreatmentPrice`;
        const fyTypeFieldId = `followUpTreatmentType`;
        resetValueObject[ftDurationFieldId] = null;
        resetValueObject[ftPriceFieldId] = null;
        resetValueObject[fyTypeFieldId] = "";
      }
    }
    
    reset(resetValueObject);
  };

  useEffect(() => {
    if(clinicRegistrationData){
      setInitialFormData()
    }
  }, [clinicRegistrationData]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleLinkButtonClick = async (field, parentFieldGroup, subFieldIndex) => {
    if(field.action === "addInitialAssessmentType"){
      // let updatedClinicDetails = Object.assign({}, JSON.parse(JSON.stringify(clinicDetailsData)));
      let updatedClinicDetails = Object.assign({}, clinicDetailsData); // deep clone
      updatedClinicDetails.sections.forEach((section) => {
        
        section.fields.forEach((fieldGroup, index) => {
          if (fieldGroup.id === "addAssessment" && fieldGroup.fields) {
            const dynamicFieldsCount = fieldGroup.fields.filter(
              (f) => f.group === "assessment"
            ).length;
            const newAssessmentGroup = {
              group: "assessment",
              deleteId: `assessment-${dynamicFieldsCount + 1}`,
              fields: [
                {
                  label: `Enter duration `,
                  placeholder: "e.g. 30",
                  unit: "mins",
                  type: "number-unit",
                  // required: true,
                  id: `initialAssessmentDuration-${dynamicFieldsCount + 1}`,
                },
                {
                  label: "Enter price",
                  placeholder: "e.g. 50",
                  unit: "£",
                  type: "number-unit",
                  // required: true,
                  id: `initialAssessmentPrice-${dynamicFieldsCount + 1}`,
                },
              ],
            };
            const newAssessmentTypeSelector = {
              label: "Select assessment type (optional)",
              placeholder: "Select an option",
              type: "dropdown",
              showInfoIcon: true,
              infoMessage: "Please select the corresponding assessment type",
              options: [
                "Home visit",
                "Virtual visit",
                "Consultant physio",
                "Senior physio",
              ],
              // required: true,
              id: `assessmentType-${dynamicFieldsCount + 1}`,
              deleteId: `assessment-${dynamicFieldsCount + 1}`,
              isDynamic: true,
            }
            
            form.setValue(`initialAssessmentDuration-${dynamicFieldsCount + 1}`, null);
            form.setValue(`initialAssessmentPrice-${dynamicFieldsCount + 1}`, null);
            const currentValues = form.getValues();
            const newSchema = generateValidationSchema(currentValues);
            setValidationSchema(newSchema); // This will re-trigger useForm with new resolver

            fieldGroup.fields.splice(subFieldIndex+index, 0, newAssessmentTypeSelector);
            fieldGroup.fields.splice(subFieldIndex+index, 0, newAssessmentGroup);
          }
        });
      });
      setClinicDetailsData(updatedClinicDetails);
    } else if(field.action === "addFollowUpTreatmentType"){
      const updatedClinicDetails = Object.assign({}, clinicDetailsData); // deep clone
      
      updatedClinicDetails.sections.forEach((section) => {
        
        section.fields.forEach((fieldGroup, index) => {
          if (fieldGroup.id === "addTreatment" && fieldGroup.fields) {
            const dynamicFieldsCount = fieldGroup.fields.filter(
              (f) => f.group === "treatment"
            ).length;
            const newTreatmentGroup = {
              group: "treatment",
              deleteId: `treatment-${dynamicFieldsCount + 1}`,
              fields: [
                {
                  label: `Enter duration`,
                  placeholder: "e.g. 30",
                  unit: "mins",
                  type: "number-unit",
                  // required: true,
                  id: `followUpTreatmentDuration-${dynamicFieldsCount + 1}`,
                },
                {
                  label: "Enter price",
                  placeholder: "e.g. 50",
                  unit: "£",
                  type: "number-unit",
                  // required: true,
                  id: `followUpTreatmentPrice-${dynamicFieldsCount + 1}`,
                },
              ],
            };
            const newTreatmentTypeSelector = {
              label: "Select follow-up treatment type (optional)",
              placeholder: "Select an option",
              type: "dropdown",
              showInfoIcon: true,
              infoMessage: "Please select the corresponding follow-up treatment type",
              options: [
                "Home visit",
                "Virtual visit",
                "Consultant physio",
                "Senior physio",
              ],
              // required: true,
              id: `followUpTreatmentType-${dynamicFieldsCount + 1}`,
              deleteId: `treatment-${dynamicFieldsCount + 1}`,
              isDynamic: true,
            };

            form.setValue(`followUpTreatmentDuration-${dynamicFieldsCount + 1}`, null);
            form.setValue(`followUpTreatmentPrice-${dynamicFieldsCount + 1}`, null);
            const currentValues = form.getValues();
            const newSchema = generateValidationSchema(currentValues);
            setValidationSchema(newSchema); // This will re-trigger useForm with new resolver

            fieldGroup.fields.splice(subFieldIndex, 0, newTreatmentTypeSelector);
            fieldGroup.fields.splice(subFieldIndex, 0, newTreatmentGroup);
          }
        });
      });
      setClinicDetailsData(updatedClinicDetails);
    } else if (field.action === "addTimeSlot") {
      const updatedClinicDetails = Object.assign({}, clinicDetailsData); // deep clone

      updatedClinicDetails.sections.forEach((section) => {
        section.fields.forEach((fieldGroup) => {
          if (fieldGroup.id === "addTimeSlotId" && fieldGroup.fields) {
            const dynamicFieldsCount = fieldGroup.fields.filter(
              (f) => f.group === "timeSlot"
            ).length;

            const newTimeSlotGroup = {
              group: "timeSlot",
              deleteId: `timeSlot-${dynamicFieldsCount + 1}`,
              fields: [
                {
                  placeholder: "Opening time",
                  type: "time",
                  // required: true,
                  id: `openingTime-${dynamicFieldsCount + 1}`,
                },
                {
                  placeholder: "Closing time",
                  type: "time",
                  // required: true,
                  id: `closingTime-${dynamicFieldsCount + 1}`,
                },
              ],
            };

            const newWeekdaySelector = {
              type: "no-border-checkbox",
              options: [{
                "label": "Mon",
                "value": "Mon"
              },
              {
                "label": "Tue",
                "value": "Tue"
              },
              {
                "label": "Wed",
                "value": "Wed"
              },
              {
                "label": "Thu",
                "value": "Thu"
              },
              {
                "label": "Fri",
                "value": "Fri"
              },
              {
                "label": "Sat",
                "value": "Sat"
              },
              {
                "label": "Sun",
                "value": "Sun"
              }],
              // required: true,
              id: `daysOfWeek-${dynamicFieldsCount + 1}`,
              deleteId: `timeSlot-${dynamicFieldsCount + 1}`,
              isDynamic: true,
              values: {
                Mon: false,
                Tue: false,
                Wed: false,
                Thu: false,
                Fri: false,
                Sat: false,
                Sun: false,
              },
            };

            form.setValue(`openingTime-${dynamicFieldsCount + 1}`, "");
            form.setValue(`closingTime-${dynamicFieldsCount + 1}`, "");
            form.setValue(`daysOfWeek-${dynamicFieldsCount + 1}`, "");
            const currentValues = form.getValues();
            const newSchema = generateValidationSchema(currentValues);
            setValidationSchema(newSchema); // This will re-trigger useForm with new resolver

            fieldGroup.fields.push(newTimeSlotGroup);
            fieldGroup.fields.push(newWeekdaySelector);
          }
        });
      });
      setClinicDetailsData(updatedClinicDetails);
    } else if (field.action === "handleImageChange") {
      setCurrentField(field);
      setSrc(field.src || null);
      setOpen(true);
    }

  };

  const handleDeleteField = (parentFieldGroup, subFieldIndex, fieldId) => {
    const updatedClinicDetails = Object.assign({}, JSON.parse(JSON.stringify(clinicDetailsData))); // deep clone;

    updatedClinicDetails.sections.forEach((section) => {
      section.fields.forEach((fieldGroup) => {
        if (fieldGroup === parentFieldGroup && Array.isArray(fieldGroup.fields)) {
          fieldGroup.fields = fieldGroup.fields.filter(field => field.deleteId !== fieldId);
        }
      });
    });

    setClinicDetailsData(updatedClinicDetails);
  };
  const handleImageSave = (croppedImageUrl) => {
    if (currentField) {
      currentField.src = croppedImageUrl;
    }
    setSrc(croppedImageUrl);
    setOpen(false);
  };

  const onSubmit = async (data) => {
    if (Object.keys(clinicRegistrationData).length === 0) {
      cogoToast.error("Please register your clinic first");
      return;
    }
    
    const assessmentType = [];
    const assessmentDuration = [];
    const assessmentPrice = [];
    const followUpTreatmentType = [];
    const followUpTreatmentDuration = [];
    const followUpTreatmentPrice = [];
    const openingTime = [];
    const closingTime = [];
    const daysOfWeek = [];

    Object.keys(data)
      .sort((a, b) => {
        const [prefixA, suffixA] = a.split('-');
        const [prefixB, suffixB] = b.split('-');

        // If both have the same prefix, sort by suffix number (default 0 if missing)
        if (prefixA === prefixB) {
          return (parseInt(suffixA) || 0) - (parseInt(suffixB) || 0);
        }

        // Otherwise, keep original order of prefixes (or you can do alphabetical)
        return a.localeCompare(b);
      }).forEach((key) => {
      if (key.startsWith('assessmentType')) {
        assessmentType.push(data[key]);
      }
      if (key.startsWith('initialAssessmentDuration')) {
        assessmentDuration.push(data[key]);
      }
      if (key.startsWith('initialAssessmentPrice')) {
        assessmentPrice.push(data[key]);
      }
      if (key.startsWith('followUpTreatmentType')) {
        followUpTreatmentType.push(data[key]);
      }
      if (key.startsWith('followUpTreatmentDuration')) {
        followUpTreatmentDuration.push(data[key]);
      }
      if (key.startsWith('followUpTreatmentPrice')) {
        followUpTreatmentPrice.push(data[key]);
      }
      if (key.startsWith('openingTime')) {
        openingTime.push(data[key]);
      }
      if (key.startsWith('closingTime')) {
        closingTime.push(data[key]);
      }
      if (key.startsWith('daysOfWeek')) {
        daysOfWeek.push(data[key]);
      }
    });

    const currentUserId = localStorage.getItem("LPCurrentUserId");
    const email = localStorage.getItem("LPCurrentUserEmail");
    let clinicId = localStorage.getItem("LPCurrentClinicId");
    const combinedAdditionalServices = Object.values(clinicRegistrationData.selectedSpecialization).flat();
    const combinedSpecialization = Object.keys(clinicRegistrationData.selectedSpecialization);
    let apiData = {
      btn_submit: true,
      provideruid: clinicRegistrationData.provideruid,
      uid: clinicRegistrationData.provideruid,
      listings: [
        {
          title: clinicRegistrationData.title,
          telephone_1: clinicRegistrationData.telephone_1,
          address_1: clinicRegistrationData.address_1,
          postcode: clinicRegistrationData.postcode,
          num_workers: clinicRegistrationData.num_workers,
          specialization: combinedSpecialization,
          treatments: combinedAdditionalServices,
          
          // email: email,
          description: data.clinicDescription,
          health_insurances_accepted: data.insuranceAccepted.concat(data.manualInsuranceEntry),
          opening_hours: openingTime.map((openTime, openTimeIndex) => {
            let retObject = {
              from: openTime || "",
              to: closingTime?.[openTimeIndex] || "",
              days: daysOfWeek?.[openTimeIndex] || "",
            };
            return retObject;
          }).filter((item) => item?.from !== ""),
          parking: data.availableParking,
          // initial_appointment_cost: data.initialAssessmentPrice,
          // initial_appointment_time: data.initialAssessmentDuration,
          // subsequent_appointment_cost: data.followUpTreatmentPrice,
          // subsequent_appointment_time: data.followUpTreatmentDuration,
          accessible_access: data.accessibleAccess,
          // address_2: clinicRegistrationData.address_2 || '',
          // location: clinicRegistrationData.location,
          // booking_management_software: clinicRegistrationData.booking_management_software,
          logo: src,
          // latitude: clinicRegistrationData?.latitude || '',
          // longitude: clinicRegistrationData?.longitude || '',
          
          
          // assessment_type: assessmentType,
          // followup_treatment_type: followUpTreatmentType,
          initial_assessment: assessmentDuration.map((duration, durationIndex) => {
            let retObject = {
              Initial_assessment_duration: duration,
              Initial_assessment_cost: assessmentPrice?.[durationIndex] || "",
              Initial_assessment_assessment_type: assessmentType?.[durationIndex] || "",
            };
            return retObject;
          }),
          follow_up_treatment: followUpTreatmentDuration.map((duration, durationIndex) => {
            let retObject = {
              follow_up_treatment_duration: duration,
              follow_up_treatment_cost: followUpTreatmentPrice?.[durationIndex] || "",
              follow_up_treatment_type: followUpTreatmentType?.[durationIndex] || "",
            };
            return retObject;
          })
        },
      ],
    };
    if(clinicId){
      apiData.listings[0].listing_id = clinicId;
      apiData.listings[0].provideruid = currentUserId;
      apiData.listings[0].uid = currentUserId;
    }else{}
    const response = await axiosProxyInstance.post('/signup/2/', apiData);
    if (response?.data?.success) {
      localStorage.setItem("currentCompleted", 2);
      localStorage.setItem("LPCurrentProvideruid", clinicRegistrationData.provideruid);
      localStorage.setItem("LPCurrentClinicTitle", clinicRegistrationData.title);
      localStorage.setItem("LPCurrentClinicId", response?.data?.data);
      goToStep(2);
    } else {
      cogoToast.error(response?.data?.data);
    }
  }

  return (
    <Box className={styles.formPageContainer}>
      <Typography variant="gotham2XL" className={styles.title}>
        {clinicDetailsData.pageTitle}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stepper 
          orientation="vertical"
          sx={{
            '& .MuiStepConnector-root': { mb: '20px' },
          }}
        >
          {clinicDetailsData.sections.map((section, index) => (
            <Step key={index} active>
              <StepLabel sx={{
                alignItems: 'flex-start',
                '& .MuiStepIcon-root': { color: 'white !important', border: '1px solid black', borderRadius: '50%' },
                '& .MuiStepIcon-text': { fill: 'black' },
              }}>
                <Typography variant="gothamLarge" className={`${styles.stepper} f-500`}>
                  {section.sectionName}
                </Typography>
                {section.introText && (
                  <Typography variant="gothamMedium" className={styles.stepper}>
                    {section.introText}
                  </Typography>
                )}
              </StepLabel>

              <StepContent>
                {section.fields.map((field, fieldIndex) => (
                  <Box key={fieldIndex} className={`${styles.stepper} margin-top-15`}>
                    {field.label && (
                      <Typography variant="gothamMedium" className="f-500" sx={field?.id === "manualInsuranceEntry"?{mb: '12px', mt: '24px'}:{mb: '12px'}}>
                        {field.label} 
                      </Typography>
                    )}
                    <Box>
                      {field.fields ? (
                        field.fields.map((subField, subFieldIndex) => {
                          if (subField.group) {
                            return (
                              <Box key={subFieldIndex} className={styles.formFields}>
                                {subField.fields.map((groupedField, groupedFieldIndex) => (
                                  <Box key={groupedFieldIndex} style={{ flex: "1" }}>
                                    {groupedField.label && (
                                      <Typography variant="gothamRegular">
                                        {groupedField.label}
                                      </Typography>
                                    )}
                                    <FormField
                                      field={groupedField}
                                      onLinkButtonClick={(clickedField) =>
                                        handleLinkButtonClick(clickedField, field, subFieldIndex)
                                      }
                                      register={register}
                                      control={control}
                                      errors={errors}
                                      watch={watch}
                                      setValue={setValue}
                                      form={form}
                                    />
                                    {groupedField.isDynamic && (
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => handleDeleteField(field, subFieldIndex, subField.deleteId)}
                                      >
                                        Delete
                                      </Button>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            );
                          }
                          return (
                            <Box key={subFieldIndex}>
                              {subField.label && (
                                <Typography variant="gothamRegular" className="margin-top-30" style={{ marginRight: '10px' }}>
                                  {subField.label}
                                  {subField.showInfoIcon && subField.infoMessage && (
                                    <Tooltip title={subField.infoMessage}>
                                      <InfoOutlinedIcon fontSize="small" className={styles.infoIcon}/>
                                    </Tooltip>
                                  )}
                                </Typography>
                              )}
                              <Box style={{ display: 'flex', alignItems: 'center' }}>
                                <FormField
                                  field={subField}
                                  onLinkButtonClick={(clickedField) =>
                                    handleLinkButtonClick(clickedField, field, subFieldIndex)
                                  }
                                  register={register}
                                  control={control}
                                  errors={errors}
                                  watch={watch}
                                  setValue={setValue}
                                  form={form}
                                  handleDeleteField={() => subField.isDynamic?handleDeleteField(field, subFieldIndex, subField.deleteId):""}
                                />

                                {/* {subField.isDynamic && (
                                  <Button
                                    color="error"
                                    size="small"
                                    className={styles.removeCircle}
                                    onClick={() => handleDeleteField(field, subFieldIndex, subField.deleteId)}
                                  >
                                    <RemoveCircleOutlineOutlinedIcon />
                                  </Button>
                                )} */}
                              </Box>
                            </Box>
                          );
                        })
                      ) : (
                        <FormField
                          field={field}
                          className={styles.formFieldContainer}
                          onLinkButtonClick={(clickedField) =>
                            handleLinkButtonClick(clickedField, section.fields, fieldIndex)
                          }
                          register={register}
                          control={control}
                          errors={errors}
                          watch={watch}
                          setValue={setValue}
                          form={form}
                        />
                      )}

                    </Box>
                  </Box>
                ))}
              </StepContent>
            </Step>
          ))}
        </Stepper>
        {
          Object.keys(errors).length > 0 && (
            <Box sx={{pl: '52px', mt: '12px'}}>
              {
                Object.entries(errors).map(([fieldId, error]) => (
                  <Box key={fieldId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <ErrorIcon fontSize="small" color="error" />
                    <Typography variant="caption" color="error">
                      {error.message}
                    </Typography>
                  </Box>
                ))
              }
            </Box>
          )
        }
        <Box className={styles.submitButtonContainer}>
          <Button
            className={styles.secondaryButton}
            size="large"
            onClick={() => goToStep(0)}
          >
            Go Back
          </Button>
          {clinicDetails.actions.map((action, actionIndex) => (
            <Button
              key={actionIndex}
              type="submit"
              className={action.variant === 'primary' ? styles.primaryButton : styles.secondaryButton}
              size="large"
            >
              {action.label}
            </Button>
          ))}
        </Box>
      </form>
      <ImageUploadModal open={open} onClose={handleClose} src={src} setSrc={setSrc} handleImageSave={handleImageSave} />
    </Box>
  );
};

export default ClinicDetails;
